import React, { FC, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap-v5'
import { KTSVG } from '../../../../_metronic/helpers'
import { showErrorAlert } from '../../../helpers/alert'
import { assignHostToExperience } from '../../../services/adminExperiencesService'
import { getHost } from '../../../services/teamExperienceService'
import { HostsAutocompleteField } from '../../teamExperiences/components/AddExperienceSteps/fields/HostsAutocompleteField'
import { shallowEqual, useSelector } from 'react-redux'
import { RootState } from '../../../../setup'
import { UserModel } from '../../../modules/auth/models/UserModel'
import { Link } from 'react-router-dom'
import { defaultProfileImage } from '../../../utils/constants'
import { EXPERIENCE_STATUS } from '../../teamExperiences/models/ExperienceStatus'

type Props = {
  open: boolean
  onClose: () => void
  experience?: any
  onSuccess: () => void
}

export const PreLaunchLeaderDrawer: FC<Props> = ({ open, onClose, experience, onSuccess }) => {
  const [loading, setLoading] = useState(false)
  const [selectedHostId, setSelectedHostId] = useState<number | null>(null)
  const [hostsList, setHostsList] = useState<any[]>([])
  const currentUser = useSelector<RootState>(({ auth }) => auth.user, shallowEqual) as UserModel

  useEffect(() => {
    if (open) {
      setSelectedHostId(null)
      getHost({ limit: 100 }).then((res) => {
        if (res?.results) setHostsList(res.results)
      })
    }
  }, [open])

  const handleApprove = async () => {
    if (!experience?.id || !selectedHostId) return
    try {
      setLoading(true)
      await assignHostToExperience(experience.id, selectedHostId)
      showErrorAlert({
        title: 'Success',
        message: 'Host has been assigned and experience status updated to Draft.',
      })
      onSuccess()
      onClose()
    } catch (err) {
      showErrorAlert({
        title: 'Error',
        message: 'Could not assign host. Please try again.',
      })
    } finally {
      setLoading(false)
    }
  }

  const adminName = currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email : 'Admin Name'

  return (
    <Modal
      show={open}
      onHide={onClose}
      backdrop='static'
      dialogClassName='experience-detail-modal'
      contentClassName='experience-detail-modal-content'
      backdropClassName='experience-detail-backdrop'
      className='w-100'
    >
      <div className='admin-experience-details-container p-0'>
        <div style={{ width: '100%', minHeight: '100%', padding: 40, background: 'white', boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.08)', borderLeft: '1px #E9E9E9 solid', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 20, display: 'inline-flex' }}>
          
          {/* Header */}
          <div style={{ alignSelf: 'stretch', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex' }}>
            <div style={{ flex: '1 1 0', color: '#151515', fontSize: 32, fontFamily: 'Oswald', fontWeight: '500', lineHeight: '38.40px' }}>
              Pre-launch Experience Requests to Lead
            </div>
            <div className='cursor-pointer' onClick={onClose} style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <KTSVG path='/media/svg/nobilis/closeX.svg' className='svg-icon-1' />
            </div>
          </div>

          {experience && (
            <>
              {/* Experience Info Card */}
              <div style={{ alignSelf: 'stretch', padding: 30, background: 'white', outline: '1px #E9E9E9 solid', outlineOffset: '-1px', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 20, display: 'flex' }}>
                
                {/* 1. Submitter / Partner Info Row */}
                {(() => {
                  const partnerName = experience?.submittedBy?.companyName || experience?.submittedBy?.company_name || experience?.companyName || experience?.company_name || experience?.submittedBy?.name || '';
                  const hostName = experience?.host?.firstName 
                    ? `${experience.host.firstName} ${experience.host.lastName || ''}`.trim() 
                    : (experience?.host?.name || 'To be assigned');

                  return (
                    <>
                      <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 10, display: 'inline-flex' }}>
                        <div style={{ color: '#808080', fontSize: 12, fontFamily: 'Satoshi', fontWeight: '300', lineHeight: '16.80px' }}>
                          Submitted by
                        </div>
                        <div className="admin-experience-details-user-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <img 
                            className="admin-experience-details-avatar" 
                            src={experience?.submittedBy?.image || defaultProfileImage} 
                            onError={(e) => {e.currentTarget.src = defaultProfileImage;}} 
                            style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }}
                          />
                          <span className="admin-experience-details-name" style={{ color: '#151515', fontSize: 12, fontFamily: 'Satoshi', fontWeight: '400', lineHeight: '16.80px' }}>
                            {partnerName}
                          </span>
                        </div>
                      </div>

                      {/* Host Info Row */}
                      <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 10, display: 'inline-flex' }}>
                        <div style={{ color: '#808080', fontSize: 12, fontFamily: 'Satoshi', fontWeight: '300', lineHeight: '16.80px' }}>
                          Host
                        </div>
                        <span style={{ color: '#151515', fontSize: 12, fontFamily: 'Satoshi', fontWeight: '500', lineHeight: '16.80px' }}>
                          {hostName}
                        </span>
                      </div>
                    </>
                  );
                })()}

                {/* 2. ID and Creation Date Row */}
                <div style={{ alignSelf: 'stretch', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex' }}>
                  <div style={{ color: '#808080', fontSize: 10, fontFamily: 'Satoshi', fontStyle: 'italic', fontWeight: '400', lineHeight: '14px' }}>
                    #{experience.id}
                  </div>
                  <div style={{ color: '#151515', fontSize: 12, fontFamily: 'Satoshi', fontWeight: '300', lineHeight: '16.80px' }}>
                    {experience.dateUploaded}
                  </div>
                </div>

                {/* 3. Title Row */}
                <div style={{ alignSelf: 'stretch', color: '#151515', fontSize: 14, fontFamily: 'Satoshi', fontWeight: '700', lineHeight: '16.80px' }}>
                  {experience.title}
                </div>

                {/* 4. Description Row */}
                <div style={{ alignSelf: 'stretch', color: '#151515', fontSize: 12, fontFamily: 'Satoshi', fontWeight: '300', lineHeight: '16.80px', whiteSpace: 'pre-line' }}>
                  {experience.description || 'No description provided.'}
                </div>

                {/* 5. Review Link Row */}
                <Link to={`/experience/detail/${experience.id}`} style={{ textDecoration: 'none' }}>
                  <div style={{ color: '#151515', fontSize: 12, fontFamily: 'Satoshi', fontWeight: '500', textDecoration: 'underline', textTransform: 'uppercase', lineHeight: '16.80px' }}>
                    Review Experience
                  </div>
                </Link>
              </div>

              {/* Host Selector Card */}
              <div style={{ alignSelf: 'stretch', padding: 30, background: 'white', outline: '1px #E9E9E9 solid', outlineOffset: '-1px', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 15, display: 'flex' }}>
                <label style={{ color: '#B4B4B4', fontSize: 10, fontFamily: 'Satoshi', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1.50px' }}>
                  Select Member Host to Lead
                </label>
                <div style={{ alignSelf: 'stretch' }}>
                  <HostsAutocompleteField
                    hosts={hostsList}
                    value={selectedHostId}
                    onChange={(id) => setSelectedHostId(id)}
                    placeholder='Search and select a member host'
                    hostType='member'
                    disabled={Number(experience?.status) === EXPERIENCE_STATUS.DRAFT}
                  />
                </div>
                <p style={{ color: '#808080', fontSize: 12, fontFamily: 'Satoshi', fontWeight: '300' }} className='mb-0'>
                  Assigning a host will transition this pre-launch experience to Draft status.
                </p>
              </div>

              {/* Footer Actions */}
              <div style={{ alignSelf: 'stretch', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex', marginTop: 20 }}>
                <div onClick={onClose} role='button' className='cursor-pointer' style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 10, paddingBottom: 10, outline: '1px #151515 solid', outlineOffset: '-1px', justifyContent: 'center', alignItems: 'center', gap: 5, display: 'flex' }}>
                  <div style={{ textAlign: 'center', color: '#151515', fontSize: 12, fontFamily: 'Oswald', fontWeight: '500', textTransform: 'uppercase', lineHeight: '22.20px' }}>
                    Cancel
                  </div>
                </div>

                <button
                  type='button'
                  disabled={!selectedHostId || loading}
                  onClick={handleApprove}
                  className='border-0 cursor-pointer'
                  style={{
                    paddingLeft: 20,
                    paddingRight: 20,
                    paddingTop: 10,
                    paddingBottom: 10,
                    background: selectedHostId && !loading ? '#151515' : '#808080',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 5,
                    display: 'flex',
                  }}
                >
                  <div style={{ textAlign: 'center', color: 'white', fontSize: 12, fontFamily: 'Oswald', fontWeight: '500', textTransform: 'uppercase', lineHeight: '22.20px' }}>
                    {!loading ? 'Approve Selected' : 'Processing...'}
                  </div>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
